import type { SortingState } from "@tanstack/react-table";
import type { ReactNode } from "react";

import { createContext, useCallback, useContext, useMemo } from "react";

import type {
  DateRange,
  Filters,
  MetricCol,
  MetricKey,
  PerformanceReportExportHandler,
  PerformanceReportExportRequest,
  PerformanceReportSelectOptions,
  PerformanceReportTableData,
  PerformanceReportTableParams,
  PerfRow,
  SummaryCard,
  ViewTab,
} from "../types";

import { formatDateWithUtcOffset } from "../../utils/date";
import {
  csvDimensionLabel,
  fmtCsv,
  hasMetric,
  metricVal,
  orderVisibleMetricCols,
  performanceCsvFileName,
} from "../helpers";
import { usePerformanceReportTranslations } from "../hooks/usePerformanceReportTranslations";
import { buildColumns } from "../table/Columns";
import {
  BasePerformanceReportProvider,
  type PerformanceReportParams,
  type PerformanceReportStates,
  type PerformanceReportView,
  useBasePerformanceReportContext,
} from "./PerformanceReportContext";

const DEFAULT_HIDDEN: Partial<Record<MetricKey, boolean>> = {
  cr_reg: true,
  cr_app: true,
  cr_funded: true,
};

const defaultRange = (): { from: Date; to: Date } => {
  const to = new Date();
  const from = new Date();
  from.setDate(to.getDate() - 30);
  return { from, to };
};

const FILTER_FNS = {
  customGlobalFilter: () => true,
  fuzzyFilter: () => true,
};

const EMPTY_TABLE_DATA: PerformanceReportTableData = {
  rows: [],
  kpiCards: [],
  lastUpdated: "",
  total: 0,
};

const toOptionList = <T extends string>(
  values: T[],
): { value: T; label: string }[] => values.map((v) => ({ value: v, label: v }));

/**
 * Authorises the selected view tab against the tabs actually on offer.
 *
 * The tab is URL-driven and the decoder is a pure wire-format codec with no
 * access to feature flags, so `?tab=campaign` decodes successfully even when
 * the host supplied no campaign options. Without this check the report would
 * render campaign-grouped data with no Campaign tab in the strip to leave it
 * by. Authorisation belongs here, where the available options are known.
 */
const authorizedTab = (
  selected: ViewTab | null | undefined,
  available: { value: ViewTab }[],
): ViewTab =>
  selected != null && available.some((option) => option.value === selected)
    ? selected
    : "source";

const noop = () => {};

/**
 * Formats the sync job's "last updated" value for display in the viewer's
 * local time, labelled with their UTC offset (e.g. "13 Jul 2026, 07:00
 * UTC+7") — matches `formatLastUpdatedTooltipDate`'s last-click tooltip
 * convention so "last updated" reads the same way everywhere in the app.
 */
const formatLastUpdatedDisplay = (value: string): string => {
  if (!value) return value;
  // Parse via native `Date` — spec-compliant, so a date-only value like
  // "2026-07-13" reads as UTC midnight rather than local midnight before
  // `formatDateWithUtcOffset` converts it to the viewer's local time.
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : formatDateWithUtcOffset(date);
};

/**
 * Reshapes the engine params into the injected CSV export handler's request.
 * `allColumns` drops the visible-column list so the mobile button exports every
 * column (the desktop button keeps `params.columns`).
 */
const toExportRequest = (
  params: PerformanceReportParams,
  allColumns: boolean,
): PerformanceReportExportRequest => ({
  tab: params.tab,
  dateRange: params.dateRange,
  filters: params.filters,
  sortColumn: params.sortColumn,
  sortDesc: params.sortDesc,
  columns: allColumns ? undefined : params.columns,
  fileName: params.fileName,
});

/**
 * Extras that don't belong to the shared report engine: the lazy
 * filter-options loading flag and a filter-modal open callback. Threaded
 * through a small local context so `usePerformanceReportContext` can expose
 * them alongside the engine-derived values (the adapter pattern from
 * CLAUDE.md).
 */
type PerformanceReportOptionsExtras = {
  isOptionsLoading: boolean;
  onFilterOpen: () => void;
  /**
   * Whether a server-side CSV export resource is wired (false in
   * storybook/mocks, where `ExportCSV` falls back to client-side generation).
   * Held in this small local context so `usePerformanceReportContext` can report
   * it without threading the raw `resources` prop through.
   */
  canExport: boolean;
  /**
   * Whether a dedicated `stats` resource is wired. When false (storybook /
   * mocks), the card loading/error/empty state and `kpiCards` fall back to the
   * table resource instead of the decoupled stats resource.
   */
  hasStatsResource: boolean;
};

const PerformanceReportOptionsExtrasContext =
  createContext<PerformanceReportOptionsExtras>({
    isOptionsLoading: false,
    onFilterOpen: noop,
    canExport: false,
    hasStatsResource: false,
  });

export type PerformanceReportResources = {
  table: {
    api: (
      params: PerformanceReportTableParams,
    ) => PerformanceReportTableData | Promise<PerformanceReportTableData>;
    shouldAutoRun: (
      next: PerformanceReportTableParams,
      prev: PerformanceReportTableParams | null,
    ) => boolean;
  };
  /**
   * Optional headline-KPI (summary card) data source, kept separate from the
   * table so the cards reload only when their own `shouldAutoRun` fires —
   * typically date-range or filter-modal changes — and NOT on tab / sort /
   * column / pagination interactions (PART-4109). When omitted (storybook /
   * mocks), the cards fall back to the table resource's `kpiCards`.
   */
  stats?: {
    api: (
      params: PerformanceReportTableParams,
    ) => SummaryCard[] | Promise<SummaryCard[]>;
    shouldAutoRun: (
      next: PerformanceReportTableParams,
      prev: PerformanceReportTableParams | null,
    ) => boolean;
  };
  /**
   * Server-side CSV export handler. One handler backs both the desktop (visible
   * columns) and mobile (all columns) buttons; the provider runs it as two
   * MANUAL engine resources (`csv` / `csvAll`). Omit in storybook/mocks to fall
   * back to `ExportCSV`'s client-side CSV generation.
   */
  csv?: { api: PerformanceReportExportHandler };
};

type PerformanceReportContextValue = {
  tab: ViewTab;
  setTab: (tab: ViewTab) => void;
  dateRange: DateRange;
  setDateRange: (range: DateRange) => void;
  resetDateRange: () => void;
  sorting: SortingState;
  setSorting: React.Dispatch<React.SetStateAction<SortingState>>;
  hidden: Partial<Record<MetricKey, boolean>>;
  toggleColumn: (key: MetricKey) => void;
  filters: Filters;
  setFilters: (filters: Filters) => void;
  kpiCards: SummaryCard[];
  columns: ReturnType<typeof buildColumns>;
  rows: PerfRow[];
  orderedCols: MetricCol[];
  tabOptions: { label: string; value: ViewTab }[];
  filterCount: number;
  csvFileName: string;
  csvDataFormatter: (row: PerfRow) => Record<string, string | number>;
  /**
   * Triggers a server-side CSV export by running the `csv` (visible columns) or
   * `csvAll` (every column) engine resource. Defaults to visible columns; pass
   * `{ allColumns: true }` (mobile) to export every column.
   */
  exportCsv: (opts?: { allColumns?: boolean }) => Promise<void>;
  /** True while a server-side export is in flight (drives the button spinner). */
  isExporting: boolean;
  /** Whether a server-side export handler is wired (false in storybook/mocks). */
  canExport: boolean;
  filterFns: typeof FILTER_FNS;
  lastUpdated: string;
  selectOptions: PerformanceReportSelectOptions;
  isError: boolean;
  isLoading: boolean;
  /**
   * First-ever load, before the report has any data. Every region renders a
   * skeleton, including the page header and controls.
   */
  isInitialLoading: boolean;
  /**
   * A reload triggered by a date / filter / sort / tab change. Only the stat
   * cards and the table re-enter the skeleton state; the chrome stays live.
   */
  isRefetching: boolean;
  /** A resolved fetch that returned no rows (never true while loading or errored). */
  isEmpty: boolean;
  /**
   * True while the headline KPI cards are (re)loading. Driven by the dedicated
   * `stats` resource, so it stays false during table-only interactions
   * (tab / sort / column / pagination) — the cards only reload on date-range or
   * filter changes (PART-4109). Falls back to `isLoading` when no stats
   * resource is wired.
   */
  isCardsLoading: boolean;
  /** The KPI cards' own fetch errored (falls back to `isError` without stats). */
  isCardsError: boolean;
  /** The KPI cards' own fetch resolved with no cards (falls back to `isEmpty`). */
  isCardsEmpty: boolean;
  retryFetch: () => void;
  /** 0-based current page index (tanstack `pageIndex`). */
  page: number;
  /** Rows per page. */
  pageSize: number;
  /** Total matching rows across all pages (from the API response). */
  totalCount: number;
  /** Set the 0-based page index (triggers a re-fetch via `shouldAutoRun`). */
  setPage: (page: number) => void;
  /** Set the page size (resets to page 0). */
  setPageSize: (pageSize: number) => void;
  /** True while the filter-options API request is in flight (first open). */
  isOptionsLoading: boolean;
  /**
   * Called each time the filter modal opens. Intended to trigger the lazy
   * fetch; must be idempotent since it fires on every open, not just the first.
   */
  onFilterOpen: () => void;
};

/** Seed shape accepted by the `initialState` prop — mirrors the engine's own
 * `initialState`, read once, lazily, on first mount only (no controlled mode,
 * no reset hatch). Callers only need to pass what they want to override; the
 * provider deep-merges it over its own hardcoded defaults. */
export type PerformanceReportInitialState = Partial<
  Omit<PerformanceReportStates, "selected">
> & {
  selected?: Partial<PerformanceReportStates["selected"]>;
};

export const PerformanceReportProvider = ({
  children,
  resources,
  selectOptions,
  isOptionsLoading = false,
  onFilterOpen,
  initialState,
  onParamsChange,
}: {
  children: ReactNode;
  resources: PerformanceReportResources;
  selectOptions: PerformanceReportSelectOptions;
  isOptionsLoading?: boolean;
  onFilterOpen?: () => void;
  /**
   * Seeds the engine's initial UI state (e.g. a deep-linked date range or
   * filter selection). Merged over this provider's own defaults — pass only
   * the fields you want to override. Read once on first mount; later prop
   * changes are ignored (matches the underlying engine's `initialState`).
   */
  initialState?: PerformanceReportInitialState;
  /**
   * Fires whenever the derived report params change (after mount, and also
   * on mount itself — see `createReportContext`'s doc comment vs. actual
   * behavior). Intended for URL sync; has no other consumer today.
   */
  onParamsChange?: (next: PerformanceReportParams) => void;
}) => {
  const translations = usePerformanceReportTranslations();

  // The campaign tab and facet are only offered when campaign options were
  // supplied (selectOptions.campaign.length > 0). This keeps the gate generic:
  // the host app provides the list, or doesn't. The provider never assumes
  // which host does what.
  const availableTabs = useMemo(
    () =>
      selectOptions.campaign.length > 0
        ? translations.tabs
        : translations.tabs.filter((tab) => tab.value !== "campaign"),
    [translations.tabs, selectOptions.campaign.length],
  );

  const innerSelectOptions = {
    viewTab: availableTabs,
    sourceType: toOptionList(selectOptions.sourceType),
    landingPage: selectOptions.landingPage,
    bannerGroup: selectOptions.bannerGroup,
    rebateProgram: selectOptions.rebateProgram,
    country: selectOptions.country,
    campaign: selectOptions.campaign,
  };

  const deriveParams = useCallback(
    (state: {
      selected: PerformanceReportParams["selected"];
      dateRange: { from: Date | null; to: Date | null };
      sortBy: string | null;
      sortOrder: "asc" | "desc" | null;
      page: number;
      pageSize: number;
      view: PerformanceReportView;
    }): PerformanceReportParams => {
      const tab = authorizedTab(state.selected.viewTab, availableTabs);
      const dateRange: DateRange = {
        startDate: state.dateRange.from,
        endDate: state.dateRange.to,
      };
      return {
        tab,
        dateRange,
        filters: {
          sourceType: state.selected.sourceType ?? [],
          landingPage: state.selected.landingPage ?? [],
          bannerGroup: state.selected.bannerGroup ?? [],
          rebateProgram: state.selected.rebateProgram ?? [],
          country: state.selected.country ?? [],
          campaign: state.selected.campaign ?? [],
        },
        sortColumn: state.sortBy,
        sortDesc: state.sortOrder == null ? null : state.sortOrder === "desc",
        // Engine `page` is 0-based (tanstack pageIndex); the API is 1-based.
        page: Math.max(state.page + 1, 1),
        pageSize: state.pageSize,
        selected: state.selected,
        // Visible column keys + filename for the CSV export resources; the table
        // resource ignores both (its shouldAutoRun doesn't compare them).
        columns: orderVisibleMetricCols(
          translations.metricColumns,
          state.view.hidden,
        ).map((c) => c.key),
        fileName: performanceCsvFileName(tab, dateRange),
      };
    },
    [translations.metricColumns, availableTabs],
  );

  const csvApi = resources.csv?.api;
  const statsResource = resources.stats;

  return (
    <PerformanceReportOptionsExtrasContext.Provider
      value={{
        isOptionsLoading,
        onFilterOpen: onFilterOpen ?? noop,
        canExport: csvApi != null,
        hasStatsResource: statsResource != null,
      }}
    >
      <BasePerformanceReportProvider
        deriveParams={deriveParams}
        resources={{
          table: {
            // The public API type allows a sync return (mock/tests rely on
            // this); the engine's ResourceApi requires a strict Promise.
            api: (params) => Promise.resolve(resources.table.api(params)),
            shouldAutoRun: resources.table.shouldAutoRun,
            // Show the skeleton on every refetch (tab/filter/date change),
            // not just the initial load.
            showLoadingOnRefetch: true,
          },
          // Headline KPI cards. AUTO resource with its own shouldAutoRun so it
          // reloads independently of the table (PART-4109). When no stats
          // source is injected (storybook/mocks), a never-run placeholder keeps
          // the engine's resource map complete; the cards then fall back to the
          // table resource's `kpiCards`.
          stats: statsResource
            ? {
                api: (params) => Promise.resolve(statsResource.api(params)),
                shouldAutoRun: statsResource.shouldAutoRun,
                showLoadingOnRefetch: true,
              }
            : {
                api: () => Promise.resolve([]),
                shouldAutoRun: () => false,
              },
          // MANUAL export resources (no shouldAutoRun) run only via
          // runResource; both delegate to the one injected handler.
          csv: {
            api: (params) =>
              Promise.resolve(csvApi?.(toExportRequest(params, false))),
          },
          csvAll: {
            api: (params) =>
              Promise.resolve(csvApi?.(toExportRequest(params, true))),
          },
        }}
        selectOptions={innerSelectOptions}
        // Deep-merges the caller's `initialState` over this provider's own
        // defaults so a caller (e.g. the portal's URL-param seeding) only
        // has to pass the fields it wants to override. Optional scalar
        // fields are spread conditionally rather than defaulted to
        // `undefined` — the engine's own `useState` initializer spreads its
        // defaults first, so an explicit `undefined` key here would
        // overwrite a good default (e.g. `page: 0`) with `undefined`.
        initialState={{
          dateRange: initialState?.dateRange ?? defaultRange(),
          sortBy: initialState?.sortBy ?? "clicks",
          sortOrder: initialState?.sortOrder ?? "desc",
          view: {
            ...initialState?.view,
            hidden: {
              ...DEFAULT_HIDDEN,
              ...initialState?.view?.hidden,
            },
          },
          selected: {
            viewTab: "source",
            ...initialState?.selected,
          },
          ...(initialState?.searchQuery !== undefined && {
            searchQuery: initialState.searchQuery,
          }),
          ...(initialState?.page !== undefined && {
            page: initialState.page,
          }),
          ...(initialState?.pageSize !== undefined && {
            pageSize: initialState.pageSize,
          }),
        }}
        onParamsChange={onParamsChange}
      >
        {children}
      </BasePerformanceReportProvider>
    </PerformanceReportOptionsExtrasContext.Provider>
  );
};

export const usePerformanceReportContext =
  (): PerformanceReportContextValue => {
    const translations = usePerformanceReportTranslations();
    const { state, actions, resources, runResource, options } =
      useBasePerformanceReportContext();
    const { isOptionsLoading, onFilterOpen, canExport, hasStatsResource } =
      useContext(PerformanceReportOptionsExtrasContext);

    const { hidden } = state.view;

    // Server-side sort: derive tanstack SortingState from the engine's
    // scalar sortBy/sortOrder, and translate table updates back into setSort.
    const sorting = useMemo<SortingState>(
      () =>
        state.sortBy != null && state.sortOrder != null
          ? [{ id: state.sortBy, desc: state.sortOrder === "desc" }]
          : [],
      [state.sortBy, state.sortOrder],
    );

    const setSorting = useCallback<
      React.Dispatch<React.SetStateAction<SortingState>>
    >(
      (updater) => {
        const next = typeof updater === "function" ? updater(sorting) : updater;
        const first = next[0];
        if (!first) {
          actions.setSort(null, null);
          return;
        }
        actions.setSort(first.id, first.desc ? "desc" : "asc");
      },
      [actions, sorting],
    );

    const toggleColumn = useCallback(
      (key: MetricKey) =>
        actions.setView((prev) => ({
          hidden: { ...prev.hidden, [key]: !prev.hidden[key] },
        })),
      [actions],
    );

    const selectOptions: PerformanceReportSelectOptions = useMemo(
      () => ({
        sourceType: options.sourceType.map((o) => o.value),
        landingPage: options.landingPage,
        bannerGroup: options.bannerGroup,
        rebateProgram: options.rebateProgram,
        country: options.country,
        campaign: options.campaign,
      }),
      [options],
    );

    const tableResource = resources.table;
    const tableData = tableResource.data ?? EMPTY_TABLE_DATA;
    const isError = tableResource.status === "error";
    const isLoading =
      tableResource.status === "loading" || tableResource.status === "idle";

    // Separates the two loading phases the design asks for: on first entry
    // every region is a skeleton, but once the report has data a query change
    // only re-skeletons the stat cards and the table — the surrounding chrome
    // stays live and interactive.
    //
    // `data` is the right signal because the engine carries the last successful
    // payload through both the `loading` and `error` states. `updatedAt` is not:
    // it's stamped only on success and dropped again on the next `loading`
    // transition, so every refetch would look like a first load.
    const hasLoaded = tableResource.data !== undefined;
    const isInitialLoading = isLoading && !hasLoaded;
    const isRefetching = isLoading && hasLoaded;
    // A resolved fetch that returned nothing. Guarded on `!isLoading`/`!isError`
    // so the stale rows kept during a refetch, and the empty fallback used on
    // error, are never mistaken for a genuinely empty result.
    const isEmpty = !isLoading && !isError && tableData.rows.length === 0;

    // Card (headline KPI) states are driven by the dedicated `stats` resource
    // when one is wired, so they only reflect its date-range/filter reloads and
    // ignore table interactions (tab/sort/column/pagination) (PART-4109). When
    // no stats resource is injected (storybook/mocks), the cards fall back to
    // the table resource's state and `kpiCards`.
    const statsResource = resources.stats;
    const kpiCards = hasStatsResource
      ? (statsResource.data ?? [])
      : tableData.kpiCards;
    const isCardsLoading = hasStatsResource
      ? statsResource.status === "loading" || statsResource.status === "idle"
      : isLoading;
    const isCardsError = hasStatsResource
      ? statsResource.status === "error"
      : isError;
    const isCardsEmpty = hasStatsResource
      ? statsResource.status === "success" &&
        (statsResource.data?.length ?? 0) === 0
      : isEmpty;

    const tab = authorizedTab(state.selected.viewTab, options.viewTab);
    // The selected sort/metric persists across view-tab changes (AC-9) — the
    // 2026-07-21 design audit re-verified the live canvas keeps the chosen
    // metric selected when switching Source→Country/Rebate, which is what
    // AC9 mandates; AC-4's "resets to the tab's default" text is superseded.
    const setTab = useCallback(
      (t: ViewTab) => {
        actions.setSelected("viewTab", t);
      },
      [actions],
    );

    const dateRange: DateRange = useMemo(
      () => ({ startDate: state.dateRange.from, endDate: state.dateRange.to }),
      [state.dateRange],
    );
    const setDateRange = useCallback(
      (next: DateRange) =>
        actions.setDateRange({ from: next.startDate, to: next.endDate }),
      [actions],
    );
    const resetDateRange = useCallback(() => {
      const { from, to } = defaultRange();
      actions.setDateRange({ from, to });
    }, [actions]);

    const filters: Filters = useMemo(
      () => ({
        sourceType: state.selected.sourceType ?? [],
        landingPage: state.selected.landingPage ?? [],
        bannerGroup: state.selected.bannerGroup ?? [],
        rebateProgram: state.selected.rebateProgram ?? [],
        country: state.selected.country ?? [],
        campaign: state.selected.campaign ?? [],
      }),
      [state.selected],
    );
    const setFilters = useCallback(
      (next: Filters) => {
        actions.setSelected("sourceType", next.sourceType);
        actions.setSelected("landingPage", next.landingPage);
        actions.setSelected("bannerGroup", next.bannerGroup);
        actions.setSelected("rebateProgram", next.rebateProgram);
        actions.setSelected("country", next.country);
        actions.setSelected("campaign", next.campaign);
      },
      [actions],
    );

    const retryFetch = useCallback(() => {
      void runResource("table");
      // Recover the cards too: they are an independent resource, so a Refresh
      // must also retry stats or a failed stats request would stay dashed
      // until a date/filter change (PART-4109).
      if (hasStatsResource) void runResource("stats");
    }, [runResource, hasStatsResource]);

    const orderedCols = useMemo<MetricCol[]>(
      () => orderVisibleMetricCols(translations.metricColumns, hidden),
      [hidden, translations.metricColumns],
    );

    const columns = useMemo(
      () =>
        buildColumns(
          tab,
          orderedCols,
          translations.sourceColumnLabels,
          translations.conversionTips,
          translations.unitUsd,
        ),
      [
        orderedCols,
        tab,
        translations.conversionTips,
        translations.sourceColumnLabels,
        translations.unitUsd,
      ],
    );

    // Count how many facets have at least one selection. The count is bounded by the
    // number of facets (6).
    const filterCount =
      (filters.sourceType.length > 0 ? 1 : 0) +
      (filters.landingPage.length > 0 ? 1 : 0) +
      (filters.bannerGroup.length > 0 ? 1 : 0) +
      (filters.rebateProgram.length > 0 ? 1 : 0) +
      (filters.country.length > 0 ? 1 : 0) +
      (filters.campaign.length > 0 ? 1 : 0);

    const csvFileName = performanceCsvFileName(tab, dateRange);
    const csvDataFormatter = useCallback(
      (row: PerfRow) => {
        const out: Record<string, string | number> = {
          [translations.sourceColumnLabels[tab]]: csvDimensionLabel(
            tab,
            row,
            translations.countryUnknown,
          ),
        };
        for (const c of orderedCols) {
          const header = c.menuLabel ?? c.label;
          out[header] = hasMetric(row, c.key)
            ? fmtCsv(metricVal(row, c.key), c.type)
            : "";
        }
        return out;
      },
      [
        orderedCols,
        tab,
        translations.sourceColumnLabels,
        translations.countryUnknown,
      ],
    );

    const isExporting =
      resources.csv.status === "loading" ||
      resources.csvAll.status === "loading";
    const exportCsv = useCallback(
      async (opts?: { allColumns?: boolean }) => {
        if (!canExport) return;
        await runResource(opts?.allColumns ? "csvAll" : "csv");
      },
      [canExport, runResource],
    );

    return {
      tab,
      setTab,
      dateRange,
      setDateRange,
      resetDateRange,
      sorting,
      setSorting,
      hidden,
      toggleColumn,
      filters,
      setFilters,
      columns,
      orderedCols,
      // Read from the engine's own option set, not `translations.tabs`, so the
      // rendered tab strip honours the campaign gate applied when the options
      // were built.
      tabOptions: options.viewTab,
      filterCount,
      csvFileName,
      csvDataFormatter,
      exportCsv,
      isExporting,
      canExport,
      filterFns: FILTER_FNS,
      lastUpdated: formatLastUpdatedDisplay(tableData.lastUpdated),
      kpiCards,
      rows: tableData.rows,
      selectOptions,
      isError,
      isLoading,
      isInitialLoading,
      isRefetching,
      isEmpty,
      isCardsLoading,
      isCardsError,
      isCardsEmpty,
      retryFetch,
      page: state.page,
      pageSize: state.pageSize,
      totalCount: tableData.total,
      setPage: actions.setPage,
      setPageSize: actions.setPageSize,
      isOptionsLoading,
      onFilterOpen,
    };
  };
